import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const DEFAULT_SUPPORT_WHATSAPP = '552139557800';
const BACKEND_URL =
  window.location.hostname.endsWith('trycloudflare.com')
    ? window.location.origin
    : (process.env.REACT_APP_BACKEND_URL || window.location.origin);
const API = `${BACKEND_URL}/api`;

const SUPPORT_ROUTES = [
  '/login',
  '/register',
  '/planos',
  '/dashboard',
  '/editor',
  '/gifts',
  '/guests',
  '/finances',
  '/dashboard/convites-artes',
];

const SUPPORT_TOPICS = [
  {
    id: 'pix-carteira',
    question: 'Paguei no PIX e não entrou na carteira. O que fazer?',
    answer:
      'Pagamentos via PIX aprovados entram no fluxo normal da carteira. Se houver comprovante, mas o valor não apareceu, envie o comprovante e o identificador do pagamento para o suporte validar a conciliação.',
    whatsappMessage: 'Paguei no PIX e o valor não entrou na carteira.',
  },
  {
    id: 'cartao-liberacao',
    question: 'Meu pagamento no cartão ficou em liberação. Isso é normal?',
    answer:
      'Sim. Pagamentos no cartão podem ficar em liberação até a confirmação operacional do provedor. Se o prazo parecer acima do esperado, o suporte pode verificar o status direto no provedor.',
    whatsappMessage: 'Tenho um pagamento no cartão que ficou em liberação e preciso validar o status.',
  },
  {
    id: 'convidados-rsvp',
    question: 'Não consigo confirmar presença ou mexer na lista de convidados.',
    answer:
      'Isso geralmente acontece por configuração da lista, modo de confirmação ou dados incompletos no cadastro. O suporte pode orientar rapidamente o ajuste certo para o seu caso.',
    whatsappMessage: 'Preciso de ajuda com confirmação de presença ou lista de convidados.',
  },
  {
    id: 'site-presentes',
    question: 'Preciso ajustar meu site, presentes ou informações do evento.',
    answer:
      'Se a dúvida for sobre edição do site, catálogo de presentes, textos ou blocos visuais, o suporte direciona o passo certo sem você precisar procurar em várias telas.',
    whatsappMessage: 'Preciso de ajuda para ajustar meu site ou minha lista de presentes.',
  },
  {
    id: 'login-acesso',
    question: 'Estou com problema de login, acesso ou senha.',
    answer:
      'Se você não consegue entrar, receber código ou validar a conta, o suporte pode conferir o fluxo de acesso e orientar o que fazer com mais rapidez.',
    whatsappMessage: 'Estou com problema de login, acesso ou senha.',
  },
];

function normalizeWhatsappNumber(value) {
  return String(value || '').replace(/\D/g, '');
}

function buildWhatsappLink(number, message) {
  const digits = normalizeWhatsappNumber(number);
  if (!digits) return null;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

function buildSupportMessage(coupleNames, email, subject, pathname) {
  const introduction = 'Olá! Preciso de ajuda no Virei Noiva.';
  const coupleLine = coupleNames ? `\nSomos o casal ${coupleNames}.` : '';
  const emailLine = email ? `\nE-mail da conta: ${email}` : '';
  const subjectLine = subject ? `\nAssunto: ${subject}` : '';

  return `${introduction}${coupleLine}${emailLine}${subjectLine}\nPágina: ${pathname}`;
}

function shouldShowSupportWidget(pathname) {
  return SUPPORT_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export default function SupportWidget() {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [coupleNames, setCoupleNames] = useState('');

  useEffect(() => {
    let active = true;

    if (!user?.user_id) {
      setCoupleNames('');
      return () => {
        active = false;
      };
    }

    axios
      .get(`${API}/sites/my`, { withCredentials: true })
      .then((response) => {
        if (active) {
          setCoupleNames(String(response.data?.couple_names || '').trim());
        }
      })
      .catch(() => {
        if (active) {
          setCoupleNames('');
        }
      });

    return () => {
      active = false;
    };
  }, [user?.user_id]);

  const supportWhatsappNumber = process.env.REACT_APP_SUPPORT_WHATSAPP || DEFAULT_SUPPORT_WHATSAPP;
  const supportWhatsappLink = useMemo(() => {
    return buildWhatsappLink(
      supportWhatsappNumber,
      buildSupportMessage(coupleNames, user?.email, '', location.pathname)
    );
  }, [coupleNames, location.pathname, supportWhatsappNumber, user?.email]);
  const fallbackTopicLink = useMemo(() => {
    return buildWhatsappLink(
      supportWhatsappNumber,
      buildSupportMessage(
        coupleNames,
        user?.email,
        'Meu caso não se encaixa nas opções rápidas.',
        location.pathname
      )
    );
  }, [coupleNames, location.pathname, supportWhatsappNumber, user?.email]);

  if (!shouldShowSupportWidget(location.pathname)) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group fixed bottom-5 right-5 z-40 inline-flex items-center gap-3 rounded-full border border-[#E6B8B4] bg-[linear-gradient(135deg,#FFFDFD_0%,#FFF1F0_100%)] px-3.5 py-2.5 text-left shadow-[0_18px_38px_-22px_rgba(130,65,65,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[#DFA4A0] hover:shadow-[0_22px_42px_-20px_rgba(130,65,65,0.62)] sm:bottom-6 sm:right-6"
        aria-label="Abrir suporte"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[linear-gradient(135deg,#E5A09B_0%,#C96F72_100%)] text-white shadow-[0_8px_18px_-9px_rgba(151,67,70,0.8)] transition-transform duration-200 group-hover:scale-[1.04]">
          <MessageCircle className="h-5 w-5" strokeWidth={2.2} />
        </span>
        <span className="hidden sm:block">
          <span className="block text-sm font-semibold text-[#493536]">Suporte</span>
          <span className="block text-xs text-[#876A69]">Converse com a gente</span>
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl border-[#E8D8C6] bg-[#FFFDFB] p-0">
          <div className="overflow-hidden rounded-lg">
            <div className="bg-[linear-gradient(135deg,#FFF5EF_0%,#F8EFE7_52%,#F3E7DA_100%)] px-6 py-6">
              <DialogHeader className="text-left">
                <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#B18858] shadow-sm">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <DialogTitle className="text-2xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display' }}>
                  Central de suporte
                </DialogTitle>
                <DialogDescription className="mt-2 text-sm leading-6 text-[#5E5148]">
                  Veja respostas rápidas para os problemas mais comuns e, se precisar, siga para o WhatsApp com a mensagem já direcionada.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="px-6 pb-6 pt-5">
              <div className="mb-5 rounded-2xl border border-[#EADCCA] bg-[#FFFAF5] p-4">
                <p className="text-sm font-semibold text-[#1C1C1C]">Atendimento humano</p>
                <p className="mt-1 text-sm leading-6 text-[#6B5B52]">
                  Se a resposta rápida não resolver, você pode abrir o WhatsApp já com o contexto do atendimento.
                </p>
                {supportWhatsappLink ? (
                  <a
                    href={supportWhatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  >
                    Falar no WhatsApp
                    <ChevronRight className="h-4 w-4" />
                  </a>
                ) : (
                  <p className="mt-4 text-xs font-medium text-[#A14F46]">
                    Canal de WhatsApp ainda não configurado no ambiente.
                  </p>
                )}
              </div>

              <Accordion type="single" collapsible className="rounded-2xl border border-[#EFE4D8] bg-white px-4">
                {SUPPORT_TOPICS.map((topic) => {
                  const topicLink = buildWhatsappLink(
                    supportWhatsappNumber,
                    buildSupportMessage(
                      coupleNames,
                      user?.email,
                      topic.whatsappMessage,
                      location.pathname
                    )
                  );

                  return (
                    <AccordionItem key={topic.id} value={topic.id} className="border-[#F0E6DB]">
                      <AccordionTrigger className="py-5 text-base font-semibold text-[#1C1C1C] hover:no-underline">
                        {topic.question}
                      </AccordionTrigger>
                      <AccordionContent className="pb-5">
                        <p className="mb-4 text-sm leading-6 text-[#5E5148]">
                          {topic.answer}
                        </p>
                        {topicLink && (
                          <a
                            href={topicLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#9A6B65] transition-colors hover:text-[#7F524D]"
                          >
                            Levar esse assunto para o WhatsApp
                            <ChevronRight className="h-4 w-4" />
                          </a>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>

              <div className="mt-5 rounded-2xl border border-dashed border-[#E7D5C2] bg-[#FFF8F1] p-4">
                <p className="text-sm font-semibold text-[#1C1C1C]">
                  Não encontrou sua situação?
                </p>
                <p className="mt-1 text-sm leading-6 text-[#6B5B52]">
                  Se o seu caso não se encaixa nas opções acima, você ainda pode seguir para o WhatsApp e explicar o que aconteceu.
                </p>
                {fallbackTopicLink && (
                  <a
                    href={fallbackTopicLink}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#9A6B65] transition-colors hover:text-[#7F524D]"
                  >
                    Falar sobre outro assunto
                    <ChevronRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
